import { ClipboardIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import { stringify } from 'query-string';
import React from 'react';
import { useParams } from 'react-router';
import Button from '@dashboard/components/Button';
import CheckoutListItem from '@dashboard/components/CheckoutListItem';
import Form from '@dashboard/components/Formik/Form';
import Spinner from '@dashboard/components/Spinner';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useToast } from '@dashboard/context/ToastContext';
import { apiUrl } from '@dashboard/services/env';
import request, { fetcher } from '@dashboard/services/request';
import CopyTextWithTooltip from '@dashboard/components/CopyTextWithTooltip';
import { templateCategoriesKey, templateKey, templatesKey } from '@dashboard/lib/queryKeys';
import FormListItem from '@dashboard/components/FormListItem';

const TemplateAdd = () => {
  const { token } = useAuth();
  const { emitToast } = useToast();
  const { templateId } = useParams();
  const queryClient = useQueryClient();

  const takeScreenshotMutation = useMutation({
    mutationFn: async () => {
      const response = await fetcher(`/templates/${templateId}/screenshot`, token, {
        method: 'PUT',
      });

      return response;
    },
    onSuccess: () => {
      emitToast({
        title: 'Screenshot updated',
        description: 'Wait a minute & refresh to see the changes',
        type: 'success',
      });
    },
    onError: () => {
      emitToast({
        title: 'Something went wrong',
        type: 'error',
      });
    },
  });

  const { data: categories } = useQuery({
    queryKey: templateCategoriesKey,
    queryFn: () =>
      fetcher(
        `/templates/categories?${stringify({
          sort: JSON.stringify({ order: 1 }),
          limit: 99,
        })}`,
        token,
      ),
    enabled: !!token,
  });

  const {
    data: template,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: templateKey(templateId),
    queryFn: () => fetcher(`/templates/${templateId}`, token),
    enabled: !!templateId,
  });

  const { data: generatedTemplateCopy, mutateAsync: generateTemplateCopy } = useMutation({
    mutationFn: async values => {
      const response = await fetcher(`/templates/generate-template-content`, token, {
        method: 'POST',
        body: JSON.stringify(values),
      });

      return response;
    },
    onSuccess: () => {
      emitToast({
        title: 'Template copy generated',
        type: 'success',
      });
    },
    onError: () => {
      emitToast({
        title: 'Something went wrong',
        type: 'error',
      });
    },
  });

  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Something went wrong</div>;

  return (
    <div className="mt-16">
      <div className="grid grid-cols-2 gap-4 mb-4 w-full">
        <div>
          <h1 className="text-2xl font-bold mb-4">Edit Template</h1>
          <Form
            initialValues={{
              title: template.title,
              subtitle: template.subtitle,
              slug: template.slug,
              page: template.page._id,
              category: template.category,
              publicTitle: template.publicTitle,
              publicDescription: template.publicDescription,
              displayAsUpsell: template.displayAsUpsell,
              order: template.order,
              keywords: template.keywords,
            }}
            fields={[
              {
                name: 'title',
                required: true,
                type: 'text',
                label: 'Template title',
              },
              {
                name: 'slug',
                required: true,
                type: 'text',
                label: 'Slug',
              },
              {
                name: 'page',
                required: true,
                type: 'text',
                label: 'Page ID',
              },
              {
                name: 'category',
                required: true,
                type: 'select',
                label: 'Category',
                options: [
                  {
                    label: 'Select a category',
                    value: '',
                  },
                  ...(categories?.map(category => ({
                    label: category.title,
                    value: category._id,
                  })) || []),
                ],
              },
              {
                name: 'displayAsUpsell',
                required: false,
                type: 'checkbox',
                label: 'Display as upsell',
              },
              {
                name: 'subtitle',
                required: false,
                type: 'textarea',
                label: 'Subtitle',
              },
              {
                name: 'publicTitle',
                required: false,
                type: 'text',
                label: 'Page title',
              },
              {
                name: 'publicDescription',
                required: false,
                type: 'textarea',
                label: 'Page content',
                minRows: 8,
                autosize: true,
              },
              {
                name: 'order',
                required: false,
                type: 'number',
                label: 'Order',
                placeholder: 'Automatically generated if left empty',
              },
              {
                name: 'keywords',
                required: false,
                type: 'text',
                label: 'Keywords',
                tooltip: 'Used to generate content with GPT-3.5',
              },
            ]}
            showState={false}
            onSubmit={async values => {
              const response = await request(`${apiUrl}/templates/${templateId}`, token, {
                method: 'PUT',
                body: JSON.stringify(values),
              });

              return {
                data: response.data,
                error: response,
                onSuccess: () => {
                  refetch();
                  emitToast({
                    title: 'Template saved',
                  });
                  queryClient.invalidateQueries({ queryKey: templatesKey });
                },
              };
            }}
            submitButton={{
              label: 'Save',
            }}
          />
        </div>

        <div>
          <h1 className="text-2xl font-bold mb-4">Generate template copy</h1>
          <Form
            initialValues={{
              title: template.title,
              keywords: template.keywords,
            }}
            fields={[
              {
                name: 'title',
                required: true,
                type: 'text',
                label: 'Title',
              },
              {
                name: 'keywords',
                required: false,
                type: 'text',
                label: 'Keywords',
              },
            ]}
            showState={false}
            onSubmit={async values => {
              try {
                const data = await generateTemplateCopy({
                  title: values.title,
                  keywords: values.keywords,
                });

                return {
                  data: {
                    title: data.title,
                    keywords: data.keywords,
                  },
                };
              } catch (error) {
                return {
                  error,
                };
              }
            }}
            submitButton={{
              label: 'Generate',
            }}
          />

          <hr className="my-8" />

          <div>
            {generatedTemplateCopy && (
              <div className="space-y-2">
                {Object.keys(generatedTemplateCopy).map(key => (
                  <div key={key} className="mb-2">
                    <strong>{key}</strong>
                    <div className="flex justify-between items-start border border-gray-300 rounded p-2 w-full bg-gray-50">
                      <div
                        className="whitespace-pre-line"
                        dangerouslySetInnerHTML={{
                          __html: generatedTemplateCopy[key],
                        }}
                      ></div>
                      <CopyTextWithTooltip text={generatedTemplateCopy[key]}>
                        <Button appearance="light" className="ml-1.5">
                          <ClipboardIcon className="w-4 h-4" />
                        </Button>
                      </CopyTextWithTooltip>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="mt-16">
        <h1 className="text-xl font-bold mb-4">Template page</h1>
        {template.page.type === 'form' ? (
          <FormListItem page={template.page} />
        ) : (
          <CheckoutListItem checkout={template.page} />
        )}
      </div>

      <div className="mt-16">
        <h1 className="text-xl font-bold mb-4">Checkouts created with this template</h1>

        {template.pages?.map(checkout => (
          <div key={checkout.id} className="mb-4">
            {checkout.type === 'form' ? (
              <FormListItem key={checkout._id} page={checkout} refetch={refetch} />
            ) : (
              <CheckoutListItem key={checkout._id} checkout={checkout} refetch={refetch} />
            )}
          </div>
        ))}

        {!template.pages?.length && (
          <div className="text-gray-500">No checkouts created with this template yet</div>
        )}
      </div>

      <div className="mt-16">
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-bold mb-4">Template Image</h1>
          <div className="text-gray-900 text-lg -mt-[5px]">
            <Button
              size="small"
              appearance="light"
              className={clsx('gap-x-1 ml-4')}
              onClick={() => {
                takeScreenshotMutation.mutate();
              }}
              disabled={takeScreenshotMutation.isLoading}
            >
              <ArrowPathIcon className="h-4 w-4" />
              Take new screenshot
              {takeScreenshotMutation.isLoading && <Spinner className="h-4 w-4" />}
            </Button>
          </div>
        </div>
        <img src={template.image} alt="Template" />
      </div>
    </div>
  );
};

export default TemplateAdd;
